import type { OfferLoadStatus } from '~/types/offer'
import type { SubscriptionOffer } from '~/types/subscription'
import * as Sentry from '@sentry/vue'
import { defineStore, storeToRefs } from 'pinia'
import { useTranslations } from '~/composables/translate/translations'
import { useAppStore } from '~/stores/appstore'
import { useLocationStore } from '~/stores/location'
import { usePaymentProfileStore } from '~/stores/payment-profile'
import { useSubscriptionDateTimeStore } from '~/stores/subscription-datetime'
import { useSubscriptionVehiclesStore } from '~/stores/subscription-vehicles'

// TODO: Switch to ResponseError once generated client is used
// 404 means no offers found for the search criteria
// All other errors (401 auth, 422 validation, 5xx server) are system errors.
function classifyOfferError(error: unknown): OfferLoadStatus {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status
    if (status === 404) {
      return 'no-offers'
    }
  }
  return 'error'
}

export const useSubscriptionOfferStore = defineStore('subscription-offer', () => {
  const offers = ref<SubscriptionOffer[]>([])
  const selectedOffer = ref<SubscriptionOffer | undefined>()
  const loadingOffers = ref(false)
  const offerLoadStatus = ref<OfferLoadStatus>('idle')
  const mileageVisited = ref(false)

  const locationStore = useLocationStore()
  const dateTimeStore = useSubscriptionDateTimeStore()
  const appStore = useAppStore()
  const { allBuildSeries } = storeToRefs(useSubscriptionVehiclesStore())
  const { selectedPaymentProfile } = storeToRefs(usePaymentProfileStore())
  const { language } = useTranslations()

  async function loadOffers(): Promise<void> {
    const pickupAt = new Date(dateTimeStore.startDateTime)

    offers.value = []
    loadingOffers.value = true
    offerLoadStatus.value = 'idle'

    try {
      // TODO: Use generated API client once endpoint is in OpenAPI spec
      const response = await $fetch<{ offers: SubscriptionOffer[] }>('/api/subscription/v1/offers', {
        method: 'POST',
        headers: {
          'Accept-Language': language.value,
          'Authorization': `Bearer ${appStore.accessToken}`,
        },
        body: {
          location: locationStore.city.id,
          build_series: allBuildSeries.value,
          pickup_at: pickupAt.toISOString(),
          duration_in_months: dateTimeStore.durationMonths,
          payment_profile_id: selectedPaymentProfile.value?.paymentProfileId,
        },
      })

      offers.value = response.offers ?? []
      offerLoadStatus.value = offers.value.length > 0 ? 'idle' : 'no-offers'

      if (offers.value.length === 0) {
        selectedOffer.value = undefined
      }
      else if (selectedOffer.value) {
        // Try to preserve selection by matching both build_series and mileage
        const previousBuildSeries = selectedOffer.value.build_series
        const previousMileage = getInclusiveDistance(selectedOffer.value)

        selectedOffer.value = offers.value.find(o =>
          o.build_series === previousBuildSeries && getInclusiveDistance(o) === previousMileage,
        ) ?? categoryOffers.value.find(o => o.build_series === previousBuildSeries)
      }
      else {
        selectedOffer.value = categoryOffers.value[0]
      }
    }
    catch (error) {
      Sentry.captureException(error)
      offerLoadStatus.value = classifyOfferError(error)
    }
    finally {
      loadingOffers.value = false
    }
  }

  function getInclusiveDistance(offer: SubscriptionOffer): number {
    return Number(offer.properties.find(p => p.identifier === 'package_inclusive_distance')?.value ?? 0)
  }

  function getSubscriptionPrice(offer: SubscriptionOffer): number {
    return Number(offer.properties.find(p => p.identifier === 'subscription_price')?.value ?? 0)
  }

  function getAdditionalDistancePrice(offer: SubscriptionOffer): number | undefined {
    const val = offer.properties.find(p => p.identifier === 'additional_distance_price')?.value
    return val !== undefined ? Number(val) : undefined
  }

  // there is one offer for each mileage option so we need to pick the one with the lowest mileage as the base
  const categoryOffers = computed<SubscriptionOffer[]>(() =>
    Object.values(
      offers.value.reduce<Record<string, SubscriptionOffer>>((acc, offer) => {
        const key = offer.build_series
        const existing = acc[key]
        const mileage = getInclusiveDistance(offer)
        const existingMileage = existing ? getInclusiveDistance(existing) : Infinity
        if (!existing || mileage < existingMileage)
          acc[key] = offer
        return acc
      }, {}),
    ),
  )

  const offersForSelectedCategory = computed<SubscriptionOffer[]>(() =>
    offers.value
      .filter(o => o.build_series === selectedOffer.value?.build_series)
      .sort((a, b) => getInclusiveDistance(a) - getInclusiveDistance(b)),
  )

  function markMileageVisited() {
    mileageVisited.value = true
  }

  return { categoryOffers, offersForSelectedCategory, selectedOffer, loadingOffers, offerLoadStatus: readonly(offerLoadStatus), loadOffers, mileageVisited: readonly(mileageVisited), markMileageVisited, getInclusiveDistance, getSubscriptionPrice, getAdditionalDistancePrice }
})
